/**
 * themeColor — single source of truth for resolving the RAG status →
 * concrete-color mapping.
 *
 * Why this exists: the report tree had ad-hoc hex literals
 * (`#1D9E75` / `#EF9F27` / `#A32D2D`) sprinkled across chart
 * components, badge components, and DOCX exporters. A user-set
 * theme.positiveColor / warningColor / negativeColor never reached
 * any of those sites because each component picked its own hex.
 *
 * The default fallbacks here are colorblind-safe contrasts that
 * match the inline SVG shape legend in RiskScatterChart, so the
 * scatter dots and the bar pills agree visually even when a tenant
 * hasn't set custom theme colors.
 */
import type { ReportTheme } from '../types/reportTheme';
import type { RagStatus } from '../types/siteRag';
import { tone, toneRamp, type ToneStop } from './tone';

/** Match the shape-legend hexes in RiskScatterChart — keep in sync. */
export const DEFAULT_RAG_COLORS: Record<RagStatus, string> = {
  green: '#1D9E75',
  amber: '#EF9F27',
  red:   '#A32D2D',
};

/**
 * Resolve a RAG status to a concrete CSS color string. Theme override
 * wins; falls through to DEFAULT_RAG_COLORS when the override is
 * missing or empty.
 *
 * Pure / synchronous so it can be inlined into recharts/SVG props
 * without re-render churn.
 */
export function ragColor(status: RagStatus, theme: Pick<ReportTheme, 'positiveColor' | 'warningColor' | 'negativeColor'>): string {
  const trim = (s: string | undefined) => (s && s.trim() ? s.trim() : undefined);
  switch (status) {
    case 'green': return trim(theme.positiveColor) ?? DEFAULT_RAG_COLORS.green;
    case 'amber': return trim(theme.warningColor)  ?? DEFAULT_RAG_COLORS.amber;
    case 'red':   return trim(theme.negativeColor) ?? DEFAULT_RAG_COLORS.red;
    default:
      // Defensive — RagStatus is a closed union but a stale persisted
      // value from a previous schema could surface here. Fall through
      // to the warning color so the cell isn't invisible.
      return trim(theme.warningColor) ?? DEFAULT_RAG_COLORS.amber;
  }
}

/** Sleek Interface (SLK-T) intent buckets that primitives consume.
 *  `primary` is the tenant brand; the other three mirror the RAG colors
 *  so a single helper covers KPIs, alerts, badges, and DOCX shading. */
export type ToneIntent = 'primary' | 'positive' | 'warning' | 'negative';

/**
 * Tone-stop palette derived from theme. Used by Sleek primitives
 * (SleekKPI, SleekAlert, SleekTOC active state, SleekChartTooltip dots,
 * DOCX shading.fill) so a tenant with primaryColor #D97706 gets
 * coherent 50/100/600/900 tints throughout the report — not raw
 * Tailwind blue/emerald/amber/rose.
 *
 * Default fallback hexes are chosen to harmonize with the existing
 * DEFAULT_THEME palette: blue/emerald/amber/red — saturated enough
 * that tone() produces useful 50/100 washes and readable 600/900
 * ink without further tuning.
 */
export function tonedTheme(
  theme: Pick<ReportTheme, 'primaryColor' | 'positiveColor' | 'warningColor' | 'negativeColor'>
): Record<ToneIntent, Record<ToneStop, string>> {
  return {
    primary:  toneRampOrDefault(theme.primaryColor,  '#0071df'),
    positive: toneRampOrDefault(theme.positiveColor, '#059669'),
    warning:  toneRampOrDefault(theme.warningColor,  '#d97706'),
    negative: toneRampOrDefault(theme.negativeColor, '#dc2626'),
  };
}

function toneRampOrDefault(input: string | undefined, fallback: string): Record<ToneStop, string> {
  const hex = (input && input.trim()) || fallback;
  const stops: ToneStop[] = [50, 100, 200, 600, 700, 800, 900];
  const out: Partial<Record<ToneStop, string>> = {};
  for (const s of stops) {
    try {
      out[s] = tone(hex, s);
    } catch {
      // Tenant persisted a malformed hex from a long-gone schema —
      // fall through to the default for this stop. Defensive only.
      out[s] = tone(fallback, s);
    }
  }
  return out as Record<ToneStop, string>;
}

// Re-export ToneStop so downstream Sleek packs can import the type
// from a single module (themeColor) alongside tonedTheme.
export type { ToneStop };
// Re-export toneRamp passthrough for callers that already pull from
// themeColor and want the generic ramp without a second import path.
export { toneRamp };
